import React from 'react';
import { Box, Theme, alpha } from '@mui/material';
import { ReportingPeriodGroup } from '@esg/esg-global-types';
import { CompanyContext } from '../../context/CompanyContext';
import { UserContext } from '../../context/UserContext';
import { ViewAccess, getViewAccess } from '../../util/user_access';
import AccessDenied from '../shared/AccessDenied';
import { FeedbackSnackbarContext } from '../../context/FeedbackSnackbarContext';
import LoadingScreen from '../../components/shared/loading/LoadingScreen';
import { GroupContext } from '../../context/GroupContext';
import { MetadataError } from '@ep/error-handling';
import { generateUuidV4 } from '../../util/uuid';
import { log } from '../../util/log';

import { useTheme } from '@mui/material';
import { Heading, MinorHeading, Title } from '../../components/shared/typography/PrimaryTypography';
import metric_collection_img from '../../assets/home_dashboard/metric_collection.webp';
import ArrowBackIosNewOutlinedIcon from '@mui/icons-material/ArrowBackIosNewOutlined';
import PrimaryButton from '../../components/shared/button/PrimaryButton';
import MetricCollectionMetricsMenu from '../../components/metric_collection/context/MetricCollectionMetricsMenu';
import { MetricExtended } from '../../lib/metric_capture/metric';
import {
  MetricRecordEntitiesMap,
  MetricRecordDataLevel,
  getMetricRecordEntitiesMap,
  metric_record_label
} from '../../graphql/services/metric_record.service';
import MetricCollectionPersistentContextMenu from '../../components/metric_collection/context/MetricCollectionPersistentParametersMenu';
import { MetricGroupExtended } from '../../lib/metric_capture/metric_group';
import MetricCollectionHub from '../../components/metric_collection/MetricCollectionHub';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { values } from '../../util/general_utils';
import { useModuleDurationTracking } from '../../hooks/useModuleDurationTracking';

export interface MetricRecordCaptureParameters {
  metric: MetricExtended | null;
  metric_group: MetricGroupExtended | null;
  is_quantitative: boolean | null;
  reporting_period_group: ReportingPeriodGroup | null;
  average_over_year: boolean;
  data_level: MetricRecordDataLevel | null;
}

/**
 * View for handling Qualitative and Quantitative Metric Record capturing.
 * @returns {JSX.Element}
 */
const MetricCollectionDataManagement = () => {
  const initial_entities_map: MetricRecordEntitiesMap = {
    countries: {},
    regions: {},
    divisions: {},
    subdivisions: {},
    sites: {},
    reporting_period_groups: {},
    reporting_periods: {},
    metrics: {},
    emission_factors: {},
    external_companies: {}
  };
  const initial_capture_parameters: MetricRecordCaptureParameters = {
    metric: null,
    metric_group: null,
    is_quantitative: null,
    reporting_period_group: null,
    average_over_year: false,
    data_level: 'national'
  };
  const { setFeedbackData } = React.useContext(FeedbackSnackbarContext);
  const { user } = React.useContext(UserContext);
  const { group } = React.useContext(GroupContext);
  const { company } = React.useContext(CompanyContext);

  if (!group || !company) return null;

  // Track module duration.
  useModuleDurationTracking('Sustainability', 'Metric Collection', group.id, company.id);

  const [access, setAccess] = React.useState<ViewAccess>(null);
  const [entitiesMap, setEntitiesMap] =
    React.useState<MetricRecordEntitiesMap>(initial_entities_map);
  const [captureParameters, setcCaptureParameters] = React.useState<MetricRecordCaptureParameters>(
    initial_capture_parameters
  );
  const [loadingEntities, setLoadingEntities] = React.useState<boolean>(true);
  const [showMetricRecords, setShowMetricRecords] = React.useState<boolean>(false);
  const theme: Theme = useTheme();
  const navigate: NavigateFunction = useNavigate();
  const main_content_ref: React.RefObject<HTMLDivElement> = React.useRef<HTMLDivElement>(null);

  // Navigate to data import module
  const handleImportClick = (): void => {
    navigate('/sustainability/import');
  };

  // Update the capture parameters.
  const handleParametersChange = (new_parameters: MetricRecordCaptureParameters): void => {
    setcCaptureParameters({
      ...captureParameters,
      ...new_parameters
    });
  };

  // Toggle the display of metric records.
  const handleShowMetricRecords = (show: boolean): void => {
    setShowMetricRecords(show);
  };

  // Fetch entities needed for capture parameter inputs pre metric record grid management.
  const fetchMetricRecordEntitiesMap = async () => {
    setLoadingEntities(true);
    try {
      if (group && company && user) {
        const entities_map: MetricRecordEntitiesMap = await getMetricRecordEntitiesMap(
          group.id,
          company.id,
          user
        );
        if (entities_map) {
          setEntitiesMap({
            countries: entities_map.countries,
            regions: entities_map.regions,
            divisions: entities_map.divisions,
            subdivisions: entities_map.subdivisions,
            sites: entities_map.sites,
            reporting_period_groups: entities_map.reporting_period_groups,
            reporting_periods: entities_map.reporting_periods,
            metrics: entities_map.metrics,
            emission_factors: entities_map.emission_factors,
            external_companies: entities_map.external_companies
          });
        }
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: MetricCollectionDataManagement failed while calling fetchMetricRecordEntitiesMap',
          {
            group: group,
            company: company,
            user: user
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to fetch capture entities. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    } finally {
      setLoadingEntities(false);
    }
  };

  React.useEffect(() => {
    (async () => {
      setShowMetricRecords(false);
      setEntitiesMap(initial_entities_map);
      setcCaptureParameters(initial_capture_parameters);
      if (group && company && user) {
        await fetchMetricRecordEntitiesMap();
      }
    })();
  }, [user, company, group]);

  React.useEffect(() => {
    setAccess(
      group && company && user
        ? getViewAccess(user, group.id, company.id, 'sustainability.metric_collection')
        : null
    );
  }, [user, company]);

  return !user || access === null ? (
    <LoadingScreen />
  ) : access !== 'none' ? (
    <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'visible' }}>
      <MetricCollectionMetricsMenu
        metrics={values<MetricExtended>(entitiesMap.metrics)}
        loading_metrics={loadingEntities}
        capture_parameters={captureParameters}
        main_content_ref={main_content_ref}
        handleParametersChange={handleParametersChange}
      />
      {captureParameters.metric_group !== null ? (
        // Context components for given metric.
        <Box
          ref={main_content_ref}
          sx={{
            boxSizing: 'border-box',
            mr: -3,
            mt: -3,
            mb: -3,
            flex: 1,
            overflow: 'hidden',
            height: 'fit-content',
            minHeight: '100vh'
          }}
        >
          <Box
            sx={{
              boxSizing: 'border-box',
              px: 4,
              mx: 3,
              borderBottom: `1px solid ${alpha(theme.palette.teal.dark, 0.4)}`,
              display: 'flex',
              alignItems: 'center',
              textAlign: 'left',
              height: '90px'
            }}
          >
            <MinorHeading weight="Medium">
              {`${captureParameters.metric_group.name}` +
                `${captureParameters.metric ? `: ${captureParameters.metric.name}` : ''}` +
                ` for ${company?.name}`}
            </MinorHeading>
            <PrimaryButton onClick={handleImportClick} sx={{ ml: 'auto' }}>
              Import {metric_record_label.many}
            </PrimaryButton>
          </Box>
          <MetricCollectionPersistentContextMenu
            entities_map={entitiesMap}
            capture_parameters={captureParameters}
            handleParametersChange={handleParametersChange}
            handleShowMetricRecords={handleShowMetricRecords}
          />
          {showMetricRecords && (
            <MetricCollectionHub
              capture_parameters={captureParameters}
              entities_map={entitiesMap}
            />
          )}
        </Box>
      ) : (
        // Welcome view with no metric selected.
        <Box
          ref={main_content_ref}
          sx={{
            flex: 1,
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            px: 3,
            mr: -3,
            mt: -3,
            mb: -3
          }}
        >
          <Box
            sx={{
              alignItems: 'center'
            }}
          >
            <img src={metric_collection_img} width="250px" />
            <Title weight="Bold" sx={{ mt: 8, mb: 3 }}>
              Welcome to Metric Collection
            </Title>
          </Box>
          <Heading weight="Light" sx={{ mb: 12 }}>
            Capture metric record data to power your ESG reporting and compliance needs
          </Heading>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ArrowBackIosNewOutlinedIcon color="primary" />
            <MinorHeading>Choose a metric to get started or</MinorHeading>
            <PrimaryButton onClick={handleImportClick}>Import Metric Records</PrimaryButton>
          </Box>
        </Box>
      )}
    </Box>
  ) : (
    <AccessDenied />
  );
};

export default MetricCollectionDataManagement;
